// Marketing · Listas de destinatarios y segmentos (listas dinámicas).
import type { JSX } from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Layers, List as ListIcon, Plus, Search, Trash2 } from "lucide-react";
import { api } from "../../../lib/api.ts";
import { useMe } from "../../../lib/auth.ts";
import { formatDate } from "../../../lib/format.ts";
import { Button, Card, EmptyState, ErrorState, Field, Input, Modal, Select, TableSkeleton, Textarea } from "../../../components/ui.tsx";
import { toast } from "../../../components/toast.tsx";
import { FilterPanel } from "./FilterPanel.tsx";
import { cleanCriteria, type Criteria, criteriaCount, describeCriteria, errMsg, type MktList, useMktOptions } from "./shared.tsx";

export function KindBadge({ kind }: { kind: string }): JSX.Element {
  return kind === "dynamic"
    ? <span className="badge bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200"><Layers size={12} className="mr-1" />Segmento</span>
    : <span className="badge bg-surface-hover text-ink-secondary"><ListIcon size={12} className="mr-1" />Estática</span>;
}

export function ListsPage(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isAdmin = Boolean(useMe().data?.admin);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const [creating, setCreating] = useState(false);

  const lists = useQuery<{ rows: MktList[] }>({
    queryKey: ["mkt", "lists", kind],
    queryFn: () => api.get(`/mkt/lists${kind ? `?kind=${kind}` : ""}`),
  });
  const rows = (lists.data?.rows ?? []).filter((l) => !q.trim() || l.name.toLowerCase().includes(q.trim().toLowerCase()));

  const dup = useMutation({
    mutationFn: (id: string) => api.post<{ list: MktList }>(`/mkt/lists/${id}/duplicate`, {}),
    onSuccess: () => { toast.success("Lista duplicada."); void qc.invalidateQueries({ queryKey: ["mkt"] }); },
    onError: (e) => toast.error(errMsg(e, "No se pudo duplicar.")),
  });
  const del = useMutation({
    mutationFn: (id: string) => api.del(`/mkt/lists/${id}`),
    onSuccess: () => { toast.success("Lista eliminada."); void qc.invalidateQueries({ queryKey: ["mkt"] }); },
    onError: (e) => toast.error(errMsg(e, "No se pudo eliminar.")),
  });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-72">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" aria-hidden />
          <Input className="pl-9" placeholder="Buscar lista" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Buscar lista" />
        </div>
        <Select className="w-auto" value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Tipo de lista">
          <option value="">Todas</option>
          <option value="static">Estáticas</option>
          <option value="dynamic">Segmentos</option>
        </Select>
        <div className="flex-1" />
        {isAdmin && <Button variant="primary" onClick={() => setCreating(true)}><Plus size={16} /> Nueva lista</Button>}
      </div>

      <CreateList open={creating} onClose={() => setCreating(false)} onCreated={(id) => { setCreating(false); navigate(`/marketing/contactos/listas/${id}`); }} />

      <Card className="overflow-hidden">
        {lists.isLoading ? <TableSkeleton rows={6} cols={5} />
          : lists.isError ? <ErrorState message={errMsg(lists.error, "No se pudieron cargar las listas.")} onRetry={() => void lists.refetch()} />
          : rows.length === 0 ? <EmptyState title="Sin listas" body="Crea una lista estática o guarda un filtro de contactos como segmento." />
          : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead><tr className="bg-canvas">
                  <th className="th">Nombre</th><th className="th">Tipo</th><th className="th text-right">Contactos</th>
                  <th className="th text-right">Con email</th><th className="th">Creada</th>{isAdmin && <th className="th w-24" />}
                </tr></thead>
                <tbody>
                  {rows.map((l) => (
                    <tr key={l.id} className="hover:bg-surface-hover cursor-pointer transition-colors" onClick={() => navigate(`/marketing/contactos/listas/${l.id}`)}>
                      <td className="td">
                        <div className="font-medium text-ink">{l.name}</div>
                        {l.description && <div className="text-xs text-ink-muted truncate max-w-md">{l.description}</div>}
                      </td>
                      <td className="td"><KindBadge kind={l.kind} />{l.source === "clientify" && <span className="badge bg-surface-hover text-ink-muted ml-1">Clientify</span>}</td>
                      <td className="td text-right tnum font-medium text-ink">{l.members.toLocaleString("es-CO")}</td>
                      <td className="td text-right tnum">{l.withEmail.toLocaleString("es-CO")}</td>
                      <td className="td whitespace-nowrap">{formatDate(l.createdAt)}{l.createdByName && <div className="text-xs text-ink-muted">{l.createdByName}</div>}</td>
                      {isAdmin && (
                        <td className="td whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          <Button small variant="ghost" onClick={() => dup.mutate(l.id)} aria-label={`Duplicar ${l.name}`} title="Duplicar"><Copy size={14} /></Button>
                          <Button small variant="ghost" title="Eliminar" aria-label={`Eliminar ${l.name}`}
                            onClick={() => { if (window.confirm(`¿Eliminar la lista "${l.name}"? Los contactos no se borran.`)) del.mutate(l.id); }}><Trash2 size={14} /></Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Card>
    </div>
  );
}

/** Formulario de creación: lista estática (opcionalmente con correos pegados) o segmento. */
function CreateList({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: (id: string) => void }): JSX.Element | null {
  const opts = useMktOptions();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [kind, setKind] = useState<"static" | "dynamic">("static");
  const [criteria, setCriteria] = useState<Criteria>({});
  const [emails, setEmails] = useState("");
  useEffect(() => { if (open) { setName(""); setDescription(""); setKind("static"); setCriteria({}); setEmails(""); } }, [open]);

  const preview = useQuery<{ members: number; withEmail: number }>({
    queryKey: ["mkt", "preview", JSON.stringify(criteria)],
    queryFn: () => api.post("/mkt/lists/preview", { criteria: cleanCriteria(criteria) }),
    enabled: open && kind === "dynamic" && criteriaCount(criteria) > 0,
  });

  const create = useMutation({
    mutationFn: async () => {
      const r = await api.post<{ list: MktList }>("/mkt/lists", {
        name: name.trim(), description: description.trim() || null, kind,
        ...(kind === "dynamic" ? { criteria: cleanCriteria(criteria) } : {}),
      });
      if (kind === "static" && emails.trim()) {
        const a = await api.post<{ added: number; notFoundCount: number }>(`/mkt/lists/${r.list.id}/members`, { emails });
        toast.info(`${a.added} contacto(s) añadidos${a.notFoundCount ? `; ${a.notFoundCount} correo(s) no encontrados` : ""}.`);
      }
      return r.list;
    },
    onSuccess: (l) => { toast.success("Lista creada."); onCreated(l.id); },
    onError: (e) => toast.error(errMsg(e, "No se pudo crear la lista.")),
  });
  const valid = name.trim() && (kind === "static" || criteriaCount(criteria) > 0);

  return (
    <Modal open={open} onClose={onClose} title="Nueva lista" wide
      footer={<><Button onClick={onClose}>Cancelar</Button><Button variant="primary" disabled={!valid || create.isPending} onClick={() => create.mutate()}>{create.isPending ? "Creando…" : "Crear"}</Button></>}>
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Nombre" required><Input value={name} maxLength={120} onChange={(e) => setName(e.target.value)} autoFocus /></Field>
          <Field label="Tipo">
            <Select value={kind} onChange={(e) => setKind(e.target.value as "static" | "dynamic")}>
              <option value="static">Estática (miembros fijos)</option>
              <option value="dynamic">Segmento (dinámica, por filtros)</option>
            </Select>
          </Field>
        </div>
        <Field label="Descripción"><Textarea rows={2} maxLength={1000} value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
        {kind === "static" ? (
          <Field label="Correos (opcional)" hint="Pega correos separados por coma, espacio o salto de línea. Solo se añaden contactos existentes.">
            <Textarea rows={4} value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="ana@empresa.com, luis@empresa.com" />
          </Field>
        ) : (
          <div className="space-y-2">
            <div className="text-sm text-ink-secondary">
              {criteriaCount(criteria) === 0 ? "Define al menos un filtro."
                : preview.isFetching ? "Calculando…"
                : preview.data ? <>Coinciden <b className="text-ink tnum">{preview.data.members.toLocaleString("es-CO")}</b> contactos ({preview.data.withEmail.toLocaleString("es-CO")} con email).</> : null}
            </div>
            {describeCriteria(criteria, opts.data).length > 0 && (
              <div className="flex flex-wrap gap-1">{describeCriteria(criteria, opts.data).map((c) => <span key={c} className="badge bg-surface-hover text-ink-secondary">{c}</span>)}</div>
            )}
            <div className="border border-line rounded-lg p-3">
              <FilterPanel value={criteria} options={opts.data} onApply={setCriteria} applyLabel="Usar estos filtros" />
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
